import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    investmentAmount: "",
    enquiryType: "",
    enquirerType: "",
    message: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log("Starting form submission...");
    
    try {
      console.log("Calling edge function with data:", formData);
      
      const { data, error } = await supabase.functions.invoke('asana-integration', {
        body: {
          action: 'create_contact_enquiry',
          ...formData
        }
      });

      console.log("Edge function response:", { data, error });

      if (error) {
        console.error('Error creating Asana task:', error);
        toast({
          title: "Error",
          description: `Submission failed: ${error.message || 'Unknown error'}`,
          variant: "destructive",
        });
        return;
      }

      if (data) {
        console.log("Success response:", data);

        // Send notification email to info@capeberkshire.co.uk
        const asanaTaskUrl = data.taskId
          ? `https://app.asana.com/0/1211334455521509/${data.taskId}`
          : undefined;

        try {
          await supabase.functions.invoke('send-transactional-email', {
            body: {
              templateName: 'contact-enquiry-notification',
              recipientEmail: 'info@capeberkshire.co.uk',
              idempotencyKey: `contact-notify-${data.taskId || Date.now()}`,
              templateData: {
                name: formData.name,
                email: formData.email,
                phone: formData.phone || undefined,
                investmentAmount: formData.investmentAmount || undefined,
                enquiryType: formData.enquiryType || undefined,
                enquirerType: formData.enquirerType || undefined,
                message: formData.message || undefined,
                asanaTaskUrl,
                submittedAt: new Date().toLocaleString('en-GB'),
              },
            },
          });
        } catch (emailError) {
          console.error('Failed to send notification email:', emailError);
          // Don't block the user - the Asana task was created successfully
        }

        toast({
          title: "Enquiry Submitted",
          description: "Thank you for your enquiry. We'll be in touch within 24 hours.",
        });
        
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          investmentAmount: "",
          enquiryType: "",
          enquirerType: "",
          message: ""
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: `Form submission failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
    {
      icon: MapPinIcon,
      title: "Office Location",
      details: ["Scotch Corner, London Road", "Sunningdale, Berkshire", "SL5 0ER", "United Kingdom"]
    },
    {
      icon: PhoneIcon,
      title: "Phone",
      details: ["+44 1344 888785", "Mon-Fri: 8:00 AM - 5:00 PM"]
    },
    {
      icon: MailIcon,
      title: "Email",
      details: ["info@capeberkshire.co.uk", "We respond within 24 hours"]
    },
    {
      icon: ClockIcon,
      title: "Business Hours",
      details: ["Monday - Friday: 8:00 AM - 5:00 PM", "Saturday: Closed", "Sunday: Closed"]
    }
  ];

  return (
    <div className="py-8">
      <SEO
        title="Contact Cape Berkshire Asset Management"
        description="Get in touch with CBAM's investment team. Speak to us about Model Portfolio Services, bespoke portfolios and adviser support across the UK."
        path="/contact"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
              Ready to start your investment journey? Get in touch with our team of professionals 
              for a free, no-obligation consultation.
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Get In Touch</CardTitle>
                <CardDescription>
                  Fill out the form below and one of our investment professionals will contact you 
                  within 24 hours to discuss your requirements.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="investment-amount">Investment Amount</Label>
                      <Select onValueChange={(value) => handleInputChange("investmentAmount", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select amount range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10k-25k">£10,000 - £25,000</SelectItem>
                          <SelectItem value="25k-50k">£25,000 - £50,000</SelectItem>
                          <SelectItem value="50k-100k">£50,000 - £100,000</SelectItem>
                          <SelectItem value="100k-250k">£100,000 - £250,000</SelectItem>
                          <SelectItem value="250k-500k">£250,000 - £500,000</SelectItem>
                          <SelectItem value="500k+">£500,000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="enquiry-type">Enquiry Type</Label>
                      <Select onValueChange={(value) => handleInputChange("enquiryType", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select enquiry type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="model-portfolios">Model Portfolios</SelectItem>
                          <SelectItem value="financial-planning">Financial Planning</SelectItem>
                          <SelectItem value="wealth-management">Wealth Management</SelectItem>
                          <SelectItem value="pension-planning">Pension Planning</SelectItem>
                          <SelectItem value="isa-planning">ISA Planning</SelectItem>
                          <SelectItem value="general">General Enquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="enquirer-type">Enquirer Type</Label>
                      <Select onValueChange={(value) => handleInputChange("enquirerType", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select enquirer type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="End-Client">End-Client</SelectItem>
                          <SelectItem value="Adviser">Adviser</SelectItem>
                          <SelectItem value="Investment Firm">Investment Firm</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      placeholder="Tell us about your investment goals and any specific requirements..."
                      rows={5}
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full">
                    Submit Enquiry
                  </Button>

                  <p className="text-sm text-muted-foreground text-center">
                    By submitting this form, you consent to us contacting you about our services. 
                    We respect your privacy and will never share your information with third parties.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold mb-6">Get In Touch</h2>
              <p className="text-muted-foreground mb-8">
                We're here to help you make informed investment decisions. Our team of experienced 
                professionals is ready to discuss your financial goals and recommend the most 
                suitable portfolio for your needs.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {contactInfo.map((info, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <info.icon className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{info.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-sm text-muted-foreground">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start" asChild>
                  <a href="tel:+441344888785">
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    Call Us Now
                  </a>
                </Button>
                <Button variant="outline" className="w-full justify-start" asChild>
                  <a href="mailto:info@capeberkshire.co.uk">
                    <MailIcon className="h-4 w-4 mr-2" />
                    Send Email
                  </a>
                </Button>
                <Button variant="outline" className="w-full justify-start" asChild>
                  <a 
                    href="https://maps.google.com/?q=Scotch Corner, London Road, Sunningdale, Berkshire, SL5 0ER, UK"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    Get Directions
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Get quick answers to common questions about our services and investment process.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What is the minimum investment?</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Our minimum investment varies by portfolio type, starting from £10,000 for our 
                  Conservative Growth portfolio. This allows us to provide proper diversification 
                  and ongoing management.
                </CardDescription>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How are fees calculated?</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  We charge a transparent annual management fee based on your portfolio value, 
                  typically ranging from 0.75% to 1.25%. There are no hidden fees or charges, 
                  and we provide full fee disclosure.
                </CardDescription>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How often do you review portfolios?</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  We conduct formal portfolio reviews quarterly, with ongoing monitoring and 
                  rebalancing as needed. Clients receive detailed quarterly reports and can 
                  request additional reviews at any time.
                </CardDescription>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Is my money protected?</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Yes, as an FCA-regulated firm, your investments are protected under the Financial 
                  Services Compensation Scheme (FSCS) up to £85,000. We also maintain comprehensive 
                  professional indemnity insurance.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;